"use client";
import React from "react";

import { Box, BoxDiv, BoxSx, IconNameType, ThemeColors } from "..";
import { useIconCache } from "./IconContext";

export interface SvgIconProps {
  fill?: "none" | "filled" | string;
}

export type IconProps = {
  size?: "xxs" | "xs" | "sm" | "md" | "lg" | "xl";
  weight?: "regular" | "filled";
  icon: IconNameType;
  color?: ThemeColors;
} & Omit<BoxDiv, "size">;

export const Icon: React.FC<IconProps> = ({
  size = "md",
  weight = "regular",
  sx,
  icon,
  color,
  ...restProps
}) => {
  const { getIconComponent, cacheIconComponent } = useIconCache();
  const LazyIconVariant = getIconComponent(icon);

  React.useEffect(() => {
    cacheIconComponent(icon, LazyIconVariant);
  }, [icon, LazyIconVariant, cacheIconComponent]);

  const iconStyle = React.useMemo<BoxSx>(() => {
    switch (size) {
      case "xxs":
        return { width: "spacing-5", height: "spacing-5" };
      case "xs":
        return { width: "spacing-6", height: "spacing-6" };
      case "sm":
        return { width: "spacing-7", height: "spacing-7" };
      case "lg":
        return { width: "spacing-9", height: "spacing-9" };
      case "xl":
        return { width: "spacing-10", height: "spacing-10" };
      default:
        return { width: "spacing-8", height: "spacing-8" };
    }
  }, [size]);

  const fill = weight === "filled" ? "filled" : "none";

  return (
    <Box
      as="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: color || "color-system-gray-950",
        ...iconStyle,
        ...sx
      }}
      {...restProps}
    >
      <LazyIconVariant fill={fill} />
    </Box>
  );
};
